import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  SET_WORKSPACE_SETUP_BANNER_MINIMIZED,
  getWorkspaceSetupBannerMinimized,
  getCompanyOnboardingStatusFromState
} from '../../states/slices/globalSlice';
import { 
  X,
  ChevronDown, 
  ChevronUp,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { useOnboardingTasks } from '../../hooks/useOnboardingTasks';

const WorkspaceSetupBanner: React.FC = () => {
  const dispatch = useDispatch();
  const isMinimized = useSelector(getWorkspaceSetupBannerMinimized);
  const onboardingStatus = useSelector(getCompanyOnboardingStatusFromState);
  const { tasks, totalTasks, completedTasks, completionPercentage } = useOnboardingTasks();
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [initialExpansionDone, setInitialExpansionDone] = useState(false);

  // Toggle the banner (close/minimize)
  const handleToggle = () => {
    dispatch(SET_WORKSPACE_SETUP_BANNER_MINIMIZED(true));
  };

  // Toggle task expansion
  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Auto-expand uncompleted tasks on initial load
  useEffect(() => {
    if (!initialExpansionDone && tasks.length > 0) {
      const uncompletedTaskIds = tasks
        .filter(task => !task.completed)
        .map(task => task.id);
      setExpandedTasks(new Set(uncompletedTaskIds));
      setInitialExpansionDone(true);
    }
  }, [tasks, initialExpansionDone]);

  // Don't render if minimized, no tasks, or onboarding is complete
  if (isMinimized || tasks.length === 0 || onboardingStatus?.onboardingComplete === true) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="bg-white dark:bg-darkBg rounded-xl shadow-sm border border-gray-200 dark:border-darkBg-border overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-darkBg-border">
          <div className="flex items-center space-x-3">
            {/* Progress indicator */}
            <div className="relative flex-shrink-0">
              <svg className="w-14 h-14 transform -rotate-90">
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  stroke="currentColor"
                  strokeWidth="5"
                  fill="none"
                  className="text-gray-200 dark:text-gray-600"
                />
                <circle
                  cx="28"
                  cy="28"
                  r="24"
                  stroke="currentColor"
                  strokeWidth="5"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 24}`}
                  strokeDashoffset={`${2 * Math.PI * 24 * (1 - completionPercentage / 100)}`}
                  className="text-orange-500 transition-all duration-500 ease-out"
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-semibold text-gray-700 dark:text-darkBg-mainText">
                  {completionPercentage}%
                </span>
              </div>
            </div>
            
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Set up your workspace</h2>
              <p className="text-sm text-gray-500 dark:text-darkBg-subText">
                {completedTasks} out of {totalTasks} tasks completed
              </p>
            </div>
          </div>
          
          {/* Close button */}
          <button
            onClick={handleToggle}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label="Close workspace setup"
          >
            <X className="w-5 h-5 text-gray-400 dark:text-gray-500" />
          </button>
        </div>

        {/* Action list */}
        <div className="divide-y divide-gray-100 dark:divide-darkBg-border">
          {tasks.map((task, index) => (
            <div key={task.id} className="transition-all duration-200">
              {/* Action header */}
              <div
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer group"
                onClick={() => toggleTaskExpansion(task.id)}
              >
                <div className="flex items-baseline space-x-3 flex-1">
                  {/* Action status icon */}
                  <div className="flex  justify-center flex-shrink-0">
                    {task.completed ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <div className="w-4 h-4 bg-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">{index + 1}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Action title */}
                  <div className="flex-1">
                    <span className="font-medium text-sm text-gray-900 dark:text-darkBg-mainText block">
                      {task.title}
                    </span>
                    {expandedTasks.has(task.id) && (
                      <span className="text-sm text-gray-500 dark:text-darkBg-subText mt-1 block">
                        {task.message}
                      </span>
                    )}
                  </div>
                </div>

                {/* Expand/collapse icon */}
                <div className="flex-shrink-0 ml-2">
                  {expandedTasks.has(task.id) ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded content - only show for uncompleted tasks */}
              {expandedTasks.has(task.id) && !task.completed && (
                <div className="px-4 pb-4 pl-12 bg-gray-50 dark:bg-gray-800/50">
                  {task.actions && task.actions.length > 0 ? (
                    <div className="flex items-center gap-3">
                      {task.actions.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick();
                          }}
                          className="inline-flex items-center space-x-1.5 text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium transition-colors"
                        >
                          <span>{action.label}</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      ))}
                    </div>
                  ) : task.action ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (task.action) {
                          task.action.onClick();
                        }
                      }}
                      className="inline-flex items-center space-x-1.5 text-sm text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 font-medium transition-colors"
                    >
                      <span>{task.action.label}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSetupBanner;
